import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import React, { useMemo, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { Permissions, Roles } from '../auth/rbac'
import { useAuth } from '../auth/AuthContext'
import { api, getApiErrorMessage } from '../lib/api'
import { asCollection } from '../lib/collections'
import { formatDate } from '../lib/date'
import { useToast } from '../ui/ToastProvider'
import ListContainer from '../ui/ListContainer'
import MenuGuideButton from '../components/MenuGuideButton'
import OperatorOrgContractSelector, { normalizeActiveOrgContracts } from '../components/OperatorOrgContractSelector'

export default function OrgBannersPage() {
    const { hasAnyPermission, hasAnyRole } = useAuth()
    const { show } = useToast()
    const queryClient = useQueryClient()
    const [searchParams, setSearchParams] = useSearchParams()
    const [page, setPage] = useState(Number(searchParams.get('page') || '1'))
    const [size] = useState(Number(searchParams.get('size') || '20'))
    const [enabledFilter, setEnabledFilter] = useState(searchParams.get('enabled') || '')
    const [operatorOrgId, setOperatorOrgId] = useState(searchParams.get('orgId') || '')
    const [operatorOrgContracts, setOperatorOrgContracts] = useState([])
    const [operatorContractsLoading, setOperatorContractsLoading] = useState(false)
    const [operatorContractLoadError, setOperatorContractLoadError] = useState('')

    const canManage = hasAnyPermission
        ? hasAnyPermission([Permissions.ORG_BANNER_MANAGE, Permissions.ORG_MANAGE])
        : false

    const isOperator = Boolean(hasAnyRole?.([Roles.OPERATOR]))
    const operatorActiveOrgContracts = useMemo(
        () => normalizeActiveOrgContracts(operatorOrgContracts),
        [operatorOrgContracts],
    )

    React.useEffect(() => {
        if (!isOperator) {
            setOperatorOrgContracts([])
            setOperatorContractLoadError('')
            return undefined
        }

        let cancelled = false
        setOperatorContractsLoading(true)
        setOperatorContractLoadError('')
        api.get('/billing/admin/config/org-contracts', { __silent: true, __suppressErrorPageRedirect: true })
            .then((res) => {
                if (cancelled) return
                const payload = res?.data?.data ?? res?.data ?? []
                setOperatorOrgContracts(Array.isArray(payload) ? payload : [])
            })
            .catch((e) => {
                if (cancelled) return
                setOperatorOrgContracts([])
                setOperatorContractLoadError(getApiErrorMessage(e, '활성 조직계약 목록을 불러오지 못했습니다.'))
            })
            .finally(() => {
                if (!cancelled) setOperatorContractsLoading(false)
            })

        return () => {
            cancelled = true
        }
    }, [isOperator])

    React.useEffect(() => {
        if (!isOperator || operatorContractsLoading) return
        const selectedExists = operatorActiveOrgContracts.some((contract) => String(contract.orgId) === String(operatorOrgId))
        const nextOrgId = selectedExists ? operatorOrgId : String(operatorActiveOrgContracts[0]?.orgId || '')
        if (nextOrgId !== operatorOrgId) setOperatorOrgId(nextOrgId)
    }, [isOperator, operatorActiveOrgContracts, operatorContractsLoading, operatorOrgId])

    React.useEffect(() => {
        const next = new URLSearchParams()
        if (page && page !== 1) next.set('page', String(page))
        if (size && size !== 20) next.set('size', String(size))
        if (enabledFilter) next.set('enabled', enabledFilter)
        if (isOperator && operatorOrgId) next.set('orgId', operatorOrgId)
        setSearchParams(next, { replace: true })
    }, [page, size, enabledFilter, isOperator, operatorOrgId, setSearchParams])


    const listQuery = useQuery({
        queryKey: ['org-banners', { page, size, enabled: enabledFilter, orgId: isOperator ? operatorOrgId : '' }],
        queryFn: async () => {
            const params = {}
            params.page = page - 1
            params.size = size
            if (enabledFilter === 'true') params.enabled = true
            if (enabledFilter === 'false') params.enabled = false
            if (isOperator && operatorOrgId) params.orgId = operatorOrgId
            const res = await api.get('/banners/org', { params })
            const payload = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
            const raw = asCollection(payload)
            const totalElements = Number(payload?.totalElements ?? payload?.total ?? raw.length ?? 0)
            return { items: raw, total: totalElements }
        },
        enabled: canManage && (!isOperator || Boolean(operatorOrgId)),
        keepPreviousData: true,
    })


    const deleteMutation = useMutation({
        mutationFn: async (id) => {
            await api.delete(`/banners/org/${id}`)
        },
        onSuccess: () => {
            show('삭제되었습니다', 'success')
            queryClient.invalidateQueries(['org-banners'])
        },
        onError: () => {
            show('삭제에 실패했습니다', 'error')
        },
    })

    const toggleEnabledMutation = useMutation({
        mutationFn: async ({ id, enabled }) => {
            await api.put(`/banners/org/${id}`, { enabled: !enabled })
        },
        onSuccess: () => {
            show('변경되었습니다', 'success')
            queryClient.invalidateQueries(['org-banners'])
        },
        onError: (err) => {
            const message = err?.response?.data?.message || '변경에 실패했습니다'
            show(message, 'error')
        },
    })

    const items = listQuery.data?.items || []
    const total = listQuery.data?.total || 0

    const handleDelete = async (e, bannerId) => {
        e.stopPropagation()
        if (!window.confirm('정말 삭제하시겠습니까?')) return
        deleteMutation.mutate(bannerId)
    }

    const handleToggleEnabled = async (e, banner) => {
        e.stopPropagation()
        toggleEnabledMutation.mutate({ id: banner.id, enabled: banner.enabled })
    }

    const selectedOrgQuery = isOperator && operatorOrgId ? `?orgId=${encodeURIComponent(operatorOrgId)}` : ''
    const orgBannerCreatePath = `/org-banners/new${selectedOrgQuery}`

    const header = (
        <div className="p-4 border-b bg-white">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                <div>
                    <div className="flex flex-wrap items-center gap-2">
                        <h1 className="text-xl font-bold">조직 배너 관리</h1>
                        <MenuGuideButton slug="org-banners" title="조직배너 가이드" />
                    </div>
                </div>
                {canManage && (!isOperator || operatorOrgId) ? (
                    <Link
                        to={orgBannerCreatePath}
                        className="inline-flex items-center gap-2 rounded-lg bg-brand-sky px-4 py-2 text-sm font-semibold text-white hover:bg-brand-sky/90"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4">
                            <path d="M10.75 4.75a.75.75 0 0 0-1.5 0v4.5h-4.5a.75.75 0 0 0 0 1.5h4.5v4.5a.75.75 0 0 0 1.5 0v-4.5h4.5a.75.75 0 0 0 0-1.5h-4.5v-4.5Z" />
                        </svg>
                        작성
                    </Link>
                ) : canManage ? (
                    <button
                        type="button"
                        disabled
                        className="inline-flex cursor-not-allowed items-center gap-2 rounded-lg bg-slate-200 px-4 py-2 text-sm font-semibold text-slate-500"
                    >
                        작성
                    </button>
                ) : null}
            </div>
            <div className="flex flex-wrap items-center gap-3">
                {isOperator && (
                    <OperatorOrgContractSelector
                        id="org-banners-operator-org"
                        contracts={operatorActiveOrgContracts}
                        value={operatorOrgId}
                        loading={operatorContractsLoading}
                        onChange={(value) => {
                            setOperatorOrgId(value)
                            setPage(1)
                        }}
                    />
                )}
                <label className="text-sm font-semibold text-slate-700" htmlFor="org-banners-enabled-filter">활성화</label>
                <select
                    id="org-banners-enabled-filter"
                    value={enabledFilter}
                    onChange={(e) => {
                        setEnabledFilter(e.target.value)
                        setPage(1)
                    }}
                    className="rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-brand-sky focus:outline-none focus:ring-2 focus:ring-brand-sky/20"
                >
                    <option value="">전체</option>
                    <option value="true">활성</option>
                    <option value="false">비활성</option>
                </select>
            </div>
            {operatorContractLoadError && (
                <div className="mt-3 rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                    {operatorContractLoadError}
                </div>
            )}
        </div>
    )


    const table = items.length > 0 ? (
        <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-slate-600">
                <tr>
                    <th className="px-4 py-2 text-left">제목</th>
                    <th className="px-4 py-2 text-left">이미지</th>
                    <th className="px-4 py-2 text-left">링크</th>
                    <th className="px-4 py-2 text-left">활성화</th>
                    <th className="px-4 py-2 text-left">우선순위</th>
                    <th className="px-4 py-2 text-left">기간</th>
                    <th className="px-4 py-2 text-left">작성일</th>
                    {canManage && (
                        <th className="px-4 py-2 text-right">관리</th>
                    )}
                </tr>
            </thead>
            <tbody>
                {items.map((banner) => (
                    <tr key={banner.id} className="border-b hover:bg-slate-50">
                        <td className="px-4 py-2">
                            <div className="font-medium">{banner.title || '(제목 없음)'}</div>
                            {banner.description && (
                                <div className="text-xs text-slate-500 mt-1 line-clamp-1">{banner.description}</div>
                            )}
                        </td>
                        <td className="px-4 py-2">
                            <div className="flex items-center gap-2">
                                {banner.imageFileIdPc && <span className="text-xs text-slate-600">PC</span>}
                                {banner.imageFileIdMobile && <span className="text-xs text-slate-600">Mobile</span>}
                                {banner.imageFileIdApp && <span className="text-xs text-slate-600">APP</span>}
                            </div>
                        </td>
                        <td className="px-4 py-2">
                            {banner.targetUrl ? (
                                <a
                                    href={banner.targetUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-xs text-brand-sky hover:underline truncate max-w-xs block"
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    {banner.targetUrl}
                                </a>
                            ) : (
                                <span className="text-xs text-slate-400">-</span>
                            )}
                        </td>
                        <td className="px-4 py-2">
                            <button
                                type="button"
                                onClick={(e) => handleToggleEnabled(e, banner)}
                                className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ${
                                    banner.enabled
                                        ? 'bg-green-100 text-green-800'
                                        : 'bg-slate-100 text-slate-800'
                                }`}
                            >
                                {banner.enabled ? '활성' : '비활성'}
                            </button>
                        </td>
                        <td className="px-4 py-2">{banner.priority || 0}</td>
                        <td className="px-4 py-2 text-xs">
                            {banner.startDate || banner.endDate ? (
                                <div>
                                    {banner.startDate ? formatDate(banner.startDate) : '무제한'} ~{' '}
                                    {banner.endDate ? formatDate(banner.endDate) : '무제한'}
                                </div>
                            ) : (
                                <span className="text-slate-400">무제한</span>
                            )}
                        </td>
                        <td className="px-4 py-2 text-xs text-slate-600">
                            {banner.createdAt ? formatDate(banner.createdAt) : '-'}
                        </td>
                        {canManage && (
                            <td className="px-4 py-2 text-right">
                                <div className="flex items-center justify-end gap-2">
                                    <Link
                                        to={`/org-banners/${banner.id}/edit${selectedOrgQuery}`}
                                        onClick={(e) => e.stopPropagation()}
                                        className="text-xs text-brand-sky hover:underline"
                                    >
                                        수정
                                    </Link>
                                    <button
                                        type="button"
                                        onClick={(e) => handleDelete(e, banner.id)}
                                        className="text-xs text-red-600 hover:underline"
                                    >
                                        삭제
                                    </button>
                                </div>
                            </td>
                        )}
                    </tr>
                ))}
            </tbody>
        </table>
    ) : (
        <div className="p-8 text-center text-slate-500">
            {isOperator && !operatorOrgId ? (operatorContractsLoading ? '활성 조직계약을 불러오는 중입니다.' : '조회할 활성 조직계약을 선택해 주세요.') : (listQuery.isLoading ? '로딩 중...' : '등록된 조직 배너가 없습니다.')}
        </div>
    )

    const footer = total > 0 ? (
        <div className="p-4 border-t bg-white flex items-center justify-between">
            <div className="text-sm text-slate-600">
                총 {total}개 중 {items.length}개 표시
            </div>
            <div className="flex items-center gap-2">
                <button
                    type="button"
                    onClick={() => setPage(Math.max(1, page - 1))}
                    disabled={page <= 1}
                    className="rounded border px-3 py-1 text-sm hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    이전
                </button>
                <span className="text-sm text-slate-600">
                    {page} / {Math.max(1, Math.ceil(total / size))}
                </span>
                <button
                    type="button"
                    onClick={() => setPage(page + 1)}
                    disabled={page >= Math.ceil(total / size)}
                    className="rounded border px-3 py-1 text-sm hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    다음
                </button>
            </div>
        </div>
    ) : null

    if (!canManage) {
        return (
            <div className="p-8 text-center text-slate-500">
                접근 권한이 없습니다.
            </div>
        )
    }

    return (
        <ListContainer header={header} table={table} footer={footer} />
    )
}
